import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { SearchableNavItem } from '../utils/navigationSearch';

interface UseCommandPaletteKeyboardReturn {
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
}

/**
 * Hook to handle command palette keyboard navigation
 *
 * @param isOpen - Whether the command palette is open
 * @param results - Current search results
 * @param onNavigate - Callback when Enter is pressed on a result
 * @param onClose - Callback when Escape is pressed
 * @returns Selected index state and setter
 */
export function useCommandPaletteKeyboard(
  isOpen: boolean,
  results: SearchableNavItem[],
  onNavigate: (path: string) => void,
  onClose: () => void
): UseCommandPaletteKeyboardReturn {
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results.length]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            onNavigate(results[selectedIndex].path);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onNavigate, onClose]);

  return { selectedIndex, setSelectedIndex };
}
